// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api/api';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : '';

// Cookie helpers
const setCookie = (name, value, days = 7) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // Use SameSite=Lax for better compatibility (allows cross-tab in most cases)
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  console.log(`[AuthContext] Set cookie ${name}`);
  console.log(`[AuthContext] All cookies:`, document.cookie.substring(0, 200));
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = decodeURIComponent(parts.pop().split(';').shift());
    console.log(`[AuthContext] Got cookie ${name}:`, cookieValue ? 'exists' : 'null');
    return cookieValue;
  }
  console.log(`[AuthContext] Cookie ${name} not found. Cookies:`, document.cookie.substring(0, 100));
  return null;
};

const removeCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  console.log(`[AuthContext] Removed cookie ${name}`);
};

// Fonction utilitaire pour vérifier la validité du token
const verifyTokenValidity = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.ok;
  } catch (error) {
    console.error('Erreur lors de la vérification du token:', error);
    return false;
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Vérifier si l'utilisateur est déjà connecté au chargement de l'application
    const validateToken = async () => {
      const token = getCookie('token');
      const storedUser = getCookie('user');

      if (token && storedUser) {
        try {
          // Vérifier la validité du token en faisant une petite requête au backend
          const isValid = await verifyTokenValidity(token);

          if (isValid) {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
          } else {
            // Token invalide ou expiré
            removeCookie('token');
            removeCookie('user');
          }
        } catch (error) {
          console.error('Erreur lors de la validation du token:', error);
          removeCookie('token');
          removeCookie('user');
        }
      }

      setLoading(false);
    };

    validateToken();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const data = await response.json();

      if (response.ok) {
        setCookie('token', data.token);
        setCookie('user', JSON.stringify(data.user));
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Erreur de connexion' };
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return { success: false, error: 'Erreur de connexion' };
    }
  };

  const register = async (email, password) => {
    try {
      const response = await authAPI.register(email, password);
      const data = await response.json();

      if (response.ok) {
        return { success: true, message: 'Inscription réussie ! Veuillez vous connecter.' };
      } else {
        return { success: false, error: data.error || 'Erreur d\'inscription' };
      }
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      return { success: false, error: 'Erreur d\'inscription' };
    }
  };

  const logout = () => {
    removeCookie('token');
    removeCookie('user');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};